import { useEffect, useState } from 'react';
import { Form, message } from 'antd';
export interface AddLineFormData {
    item_code: string;
    item_name: string;
    subs: string;
    uom: string;
    dmd: number;
    available_qty: number;
    sup: number;
    price: number;
    original_disc_percent: number;
    original_disc_amount: number;
    proposal_disc_percent?: number;
    proposal_disc_amount?: number;
    total: number;
    regional_stock: string;
    location_code_rack: string;
    remark_table: string;
}

interface AgreementData {
    id: number;
    no_agreement: string;
    cs_code: string;
    name_customer: string;
    reference_no: string;
    disc: number;
    expired: string;
}

export const useSalesAddLine = (
    onSubmit?: (data: AddLineFormData) => void,
    editData?: AddLineFormData | null,
    onCancel?: () => void,
    initialDiscPercent?: number
) => {
    const [form] = Form.useForm();
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isDiscPercentDisabled, setIsDiscPercentDisabled] = useState<boolean>(false);
    const [isDiscAmountDisabled, setIsDiscAmountDisabled] = useState<boolean>(false);

    const resetForm = () => {
        form.resetFields();
        setIsDiscPercentDisabled(false);
        setIsDiscAmountDisabled(false);
    };

    useEffect(() => {

        if (editData) {
            setIsEditMode(true);

            form.setFieldsValue({
                item_code: editData.item_code,
                item_name: editData.item_name,
                subs: editData.subs,
                uom: editData.uom,
                dmd: editData.dmd,
                available_qty: editData.available_qty,
                sup: editData.sup,
                price: editData.price,
                original_disc_percent: editData.original_disc_percent,
                original_disc_amount: editData.original_disc_amount,
                proposal_disc_percent: editData.proposal_disc_percent,
                proposal_disc_amount: editData.proposal_disc_amount,
                total: editData.total,
                regional_stock: editData.regional_stock,
                location_code_rack: editData.location_code_rack,
                remark_table: editData.remark_table
            });


            const hasOriginalDiscount = (editData.original_disc_percent || 0) > 0 || (editData.original_disc_amount || 0) > 0;


            if (hasOriginalDiscount) {
                setIsDiscPercentDisabled(true);
                setIsDiscAmountDisabled(true);
            } else {

                if ((editData.proposal_disc_percent || 0) > 0) {
                    setIsDiscAmountDisabled(true);
                    setIsDiscPercentDisabled(false);
                } else if ((editData.proposal_disc_amount || 0) > 0) {
                    setIsDiscPercentDisabled(true);
                    setIsDiscAmountDisabled(false);
                } else {
                    setIsDiscPercentDisabled(false);
                    setIsDiscAmountDisabled(false);
                }
            }
        } else {

            setIsEditMode(false);

            resetForm();

            if (initialDiscPercent !== undefined) {
                form.setFieldsValue({
                    original_disc_percent: initialDiscPercent,
                });
            }
        }
    }, [editData, initialDiscPercent, form]);

    const handleFormValuesChange = (changedValues: any, allValues: AddLineFormData) => {
        const price = allValues.price || 0;
        const dmd = allValues.dmd || 0;
        const originalDiscPercent = allValues.original_disc_percent || 0;
        let originalDiscAmount = allValues.original_disc_amount || 0;
        let proposalDiscPercent = allValues.proposal_disc_percent || 0;
        let proposalDiscAmount = allValues.proposal_disc_amount || 0;
        let updatedFields: any = {};
        let nextIsDiscPercentDisabled = false;        
        let nextIsDiscAmountDisabled = false;
        
        if ('price' in changedValues || 'original_disc_percent' in changedValues) {
            const calculatedOriginalDiscAmount = (price * originalDiscPercent) / 100;
            updatedFields.original_disc_amount = calculatedOriginalDiscAmount;           
            originalDiscAmount = calculatedOriginalDiscAmount;
        }
     
        const hasOriginalDiscount = (originalDiscPercent > 0) || (originalDiscAmount > 0);
        if (hasOriginalDiscount) {
            nextIsDiscPercentDisabled = true;
            nextIsDiscAmountDisabled = true;
            
            if ('proposal_disc_percent' in changedValues || 'proposal_disc_amount' in changedValues) {
                 updatedFields.proposal_disc_percent = 0;
                 updatedFields.proposal_disc_amount = 0;
                 proposalDiscPercent = 0;
                 proposalDiscAmount = 0;
            } else {
                 proposalDiscPercent = allValues.proposal_disc_percent || 0;
                 proposalDiscAmount = allValues.proposal_disc_amount || 0;
                 
                 if (proposalDiscPercent > 0 || proposalDiscAmount > 0) {
                     updatedFields.proposal_disc_percent = 0;
                     updatedFields.proposal_disc_amount = 0;
                     proposalDiscPercent = 0;
                     proposalDiscAmount = 0;
                 }
            }
        } else {
        
            if ('proposal_disc_percent' in changedValues) {
                proposalDiscAmount = (price * proposalDiscPercent) / 100;
                updatedFields.proposal_disc_amount = proposalDiscAmount;
                nextIsDiscAmountDisabled = proposalDiscPercent > 0;
                nextIsDiscPercentDisabled = false;

            } else if ('proposal_disc_amount' in changedValues) {
                nextIsDiscPercentDisabled = proposalDiscAmount > 0;               
                nextIsDiscAmountDisabled = false;             
            } else {
            
                if ((proposalDiscPercent || 0) > 0) {
                    
                    proposalDiscAmount = (price * proposalDiscPercent) / 100;
                    updatedFields.proposal_disc_amount = proposalDiscAmount;
                    nextIsDiscAmountDisabled = true;
                    nextIsDiscPercentDisabled = false;
                } else if ((proposalDiscAmount || 0) > 0) {
                    nextIsDiscPercentDisabled = true;
                    nextIsDiscAmountDisabled = false;
                } else {
                    nextIsDiscPercentDisabled = false;
                    nextIsDiscAmountDisabled = false;
                    proposalDiscAmount = 0;
                    updatedFields.proposal_disc_amount = proposalDiscAmount;
                }
            }
        }

        if (isDiscPercentDisabled !== nextIsDiscPercentDisabled) setIsDiscPercentDisabled(nextIsDiscPercentDisabled);
        if (isDiscAmountDisabled !== nextIsDiscAmountDisabled) setIsDiscAmountDisabled(nextIsDiscAmountDisabled);
        const currentOriginalDiscAmount = updatedFields.original_disc_amount !== undefined ? updatedFields.original_disc_amount : originalDiscAmount;
        const currentProposalDiscAmount = updatedFields.proposal_disc_amount !== undefined ? updatedFields.proposal_disc_amount : proposalDiscAmount;
        const total = (price - currentOriginalDiscAmount - currentProposalDiscAmount) * dmd;
        updatedFields.total = total;
        
        if (Object.keys(updatedFields).length > 0) {
            form.setFieldsValue(updatedFields);
        }
    };

    const handleCancelClick = () => {
        resetForm();
        if (onCancel) {
            onCancel();
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (!values.item_code) {
                message.error('Item Code is required');
                return;
            }
            if (onSubmit) {
                onSubmit(values as AddLineFormData);
            }
            resetForm();
        } catch (error) {
            console.error('Validation failed:', error);
            message.error('Please check your input');
        }
    };

    return {
        form,
        handleSubmit,
        handleCancelClick,
        handleFormValuesChange,
        isEditMode,
        isDiscPercentDisabled,
        isDiscAmountDisabled,
    };
};